'use client'

/**
 * Client-side upgrade modal hook and provider
 * 
 * Provides a React context for opening/closing the upgrade modal
 * and managing modal state across the app.
 */

import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react'
import { useSession } from 'next-auth/react'
import { UpgradeModal } from '@/components/subscription/UpgradeModal'
import { trackUpgradeClicked } from '@/lib/subscription/analytics'
import { logHydrationFirstRender } from '@/lib/hydration/useClientMounted'
import {
  logUpgradeBlockedDuringNativeOAuth,
  shouldBlockUpgradeDuringNativeOAuth,
} from '@/lib/monetization/blockUpgradeDuringNativeOAuth'
import {
  logUpgradeCtaClick,
  logUpgradeModalOpenCall,
  logUpgradeModalProviderOpen,
  WAGAMOUR_OPEN_UPGRADE_MODAL_EVENT,
} from '@/lib/monetization/tracedUpgradeModal'
import { forceClearNativeInteractionLocks } from '@/lib/monetization/forceClearNativeInteractionLocks'
import { shouldOpenBillingRecoveryInsteadOfUpgrade } from '@/components/subscription/BillingRecoveryNotice'
import {
  clearRememberedScrollYForNextLock,
  getDocumentScrollY,
  rememberScrollYForNextLock,
} from '@/lib/dom/documentScrollLock'
import {
  isPicksSkuDiagEnabled,
  recordPicksSkuDiag,
} from '@/lib/debug/picksSkuDiag'
import { detectNativeIosForUpgrade } from '@/lib/monetization/detectUpgradePlatform'
import { isNativeCapacitorApp } from '@/lib/pwa/isNativeCapacitorApp'
import { isStandalone } from '@/lib/pwa/isStandalone'

export interface OpenUpgradeModalOptions {
  source: 'parks' | 'picks' | 'chat' | 'profile' | 'other'
  feature: 
    | 'park_safety_intelligence'
    | 'why_this_park'
    | 'smart_walk_intelligence'
    | 'photo_upload'
    | 'ai_picks'
    | 'community_analysis'
    | string
  valueProposition?: string
}

/** Ephemeral CTA feedback so Free AI Fit taps never look dead. */
export const WAGAMOUR_UPGRADE_CTA_FEEDBACK_EVENT = 'wagamour:upgradeCtaFeedback'

export type UpgradeCtaFeedbackDetail = {
  state: 'pending' | 'recovery' | 'blocked' | 'error' | 'cleared'
  message: string
  feature?: string
}

interface UpgradeModalContextType {
  openUpgradeModal: (options: OpenUpgradeModalOptions) => void
  closeUpgradeModal: () => void
  isOpen: boolean
  feature: string
  valueProposition?: string
  /** True while billing-gate / open is in flight after a CTA tap. */
  ctaPending: boolean
  ctaFeedback: string | null
}

const UpgradeModalContext = createContext<UpgradeModalContextType | null>(null)

export function useUpgradeModal() {
  const context = useContext(UpgradeModalContext)
  if (!context) {
    throw new Error('useUpgradeModal must be used within UpgradeModalProvider')
  }
  return context
}

interface UpgradeModalProviderProps {
  children: ReactNode
}

function emitUpgradeCtaFeedback(detail: UpgradeCtaFeedbackDetail) {
  if (typeof window === 'undefined') return
  window.dispatchEvent(
    new CustomEvent(WAGAMOUR_UPGRADE_CTA_FEEDBACK_EVENT, { detail })
  )
}

export function UpgradeModalProvider({ children }: UpgradeModalProviderProps) {
  const { data: session } = useSession()
  const sessionUserId = (session?.user as { id?: string } | undefined)?.id
  const [isOpen, setIsOpen] = useState(false)
  const [feature, setFeature] = useState<string>('')
  const [valueProposition, setValueProposition] = useState<string | undefined>()
  const [ctaPending, setCtaPending] = useState(false)
  const [ctaFeedback, setCtaFeedback] = useState<string | null>(null)

  useEffect(() => {
    logHydrationFirstRender('UpgradeModalProvider', { isOpen, feature })
  }, [isOpen, feature])

  // Auto-clear non-pending feedback so recovery/blocked toasts don't stick forever.
  useEffect(() => {
    if (!ctaFeedback || ctaPending) return
    const id = window.setTimeout(() => {
      setCtaFeedback(null)
      emitUpgradeCtaFeedback({ state: 'cleared', message: '' })
    }, 4500)
    return () => window.clearTimeout(id)
  }, [ctaFeedback, ctaPending])

  const openUpgradeModal = useCallback((options: OpenUpgradeModalOptions) => {
    if (shouldBlockUpgradeDuringNativeOAuth()) {
      logUpgradeBlockedDuringNativeOAuth('useUpgradeModal.openUpgradeModal')
      const msg =
        'Sign-in is still finishing — try AI Fit again in a moment.'
      setCtaPending(false)
      setCtaFeedback(msg)
      emitUpgradeCtaFeedback({
        state: 'blocked',
        message: msg,
        feature: options.feature,
      })
      return
    }

    const { source, feature: featureKey, valueProposition: vp } = options
    // Capture scrollY on the tap frame — billing gate is async and iOS PWA often
    // reports scrollY=0 by the time the modal mounts (freeze-at-top bug).
    const scrollYAtTap = getDocumentScrollY()
    rememberScrollYForNextLock(scrollYAtTap)

    setCtaPending(true)
    setCtaFeedback('Checking your account…')
    emitUpgradeCtaFeedback({
      state: 'pending',
      message: 'Checking your account…',
      feature: featureKey,
    })

    logUpgradeCtaClick(source, featureKey)
    logUpgradeModalOpenCall(source, featureKey)

    if (isPicksSkuDiagEnabled()) {
      recordPicksSkuDiag({
        kind: 'decision',
        code: 'upgrade_cta_tap',
        path: 'picks',
        flags: {
          source: String(source).slice(0, 24),
          feature: String(featureKey).slice(0, 32),
          scrollYAtTap,
          isStandalone: isStandalone(),
          isNativeCap: isNativeCapacitorApp(),
          purchaseNativeIos: detectNativeIosForUpgrade(),
        },
      })
    }

    const openModal = () => {
      // Re-arm remembered Y in case a prior open/close consumed it.
      rememberScrollYForNextLock(scrollYAtTap)
      logUpgradeModalProviderOpen(source, featureKey)
      trackUpgradeClicked(featureKey, sessionUserId, source)
      setFeature(featureKey)
      setValueProposition(vp)
      setIsOpen(true)
      setCtaPending(false)
      setCtaFeedback(null)
      emitUpgradeCtaFeedback({
        state: 'cleared',
        message: '',
        feature: featureKey,
      })
      if (isPicksSkuDiagEnabled()) {
        recordPicksSkuDiag({
          kind: 'decision',
          code: 'upgrade_modal_open',
          path: 'picks',
          flags: {
            scrollYAtTap,
            scrollYNow: getDocumentScrollY(),
            purchaseNativeIos: detectNativeIosForUpgrade(),
          },
        })
      }
    }

    // Existing past_due/unpaid sub → recovery notice (no duplicate checkout).
    // Fail open to the upgrade modal if billing-status is slow/unreachable so
    // Free → AI Fit taps never silently no-op on PWA/web.
    const BILLING_GATE_MS = 600
    void Promise.race([
      shouldOpenBillingRecoveryInsteadOfUpgrade(),
      new Promise<boolean>((resolve) => {
        window.setTimeout(() => resolve(false), BILLING_GATE_MS)
      }),
    ])
      .then((useRecovery) => {
        if (useRecovery) {
          clearRememberedScrollYForNextLock()
          const msg =
            'Renewal needs attention — use Update payment method above. No page jump.'
          setCtaPending(false)
          setCtaFeedback(msg)
          emitUpgradeCtaFeedback({
            state: 'recovery',
            message: msg,
            feature: featureKey,
          })
          console.log(
            '[UpgradeTrace9419bd7] openUpgradeModal.routed_to_billing_recovery',
            { source, feature: featureKey }
          )
          if (isPicksSkuDiagEnabled()) {
            recordPicksSkuDiag({
              kind: 'decision',
              code: 'upgrade_routed_billing_recovery',
              path: 'picks',
              flags: { scrollYAtTap },
            })
          }
          return
        }
        openModal()
      })
      .catch(() => {
        openModal()
      })
  }, [sessionUserId])

  const closeUpgradeModal = useCallback(() => {
    // Unlock scroll first (modal effect), then clear Cap overlays — never the reverse.
    setIsOpen(false)
    setFeature('')
    setValueProposition(undefined)
    setCtaPending(false)
    setCtaFeedback(null)
    // Wait two frames so WagamourV2Modal unlockDocumentScroll runs first.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        forceClearNativeInteractionLocks('upgrade_modal_provider_close')
      })
    })
  }, [])

  useEffect(() => {
    const onUpgradeIntentCleared = () => {
      closeUpgradeModal()
    }
    window.addEventListener('wagamour:upgradeIntentCleared', onUpgradeIntentCleared)
    return () => {
      window.removeEventListener('wagamour:upgradeIntentCleared', onUpgradeIntentCleared)
    }
  }, [closeUpgradeModal])

  useEffect(() => {
    const onOpenUpgradeModal = (event: Event) => {
      const detail = (event as CustomEvent<OpenUpgradeModalOptions>).detail
      if (!detail?.source || !detail?.feature) return
      openUpgradeModal(detail)
    }
    window.addEventListener(WAGAMOUR_OPEN_UPGRADE_MODAL_EVENT, onOpenUpgradeModal)
    return () => {
      window.removeEventListener(WAGAMOUR_OPEN_UPGRADE_MODAL_EVENT, onOpenUpgradeModal)
    }
  }, [openUpgradeModal])

  return (
    <UpgradeModalContext.Provider
      value={{
        openUpgradeModal,
        closeUpgradeModal,
        isOpen,
        feature,
        valueProposition,
        ctaPending,
        ctaFeedback,
      }}
    >
      {children}
      {ctaFeedback ? (
        <div
          role="status"
          aria-live="polite"
          data-testid="upgrade-cta-feedback"
          className="pointer-events-none fixed bottom-20 left-1/2 z-[120000] w-[min(92vw,24rem)] -translate-x-1/2 rounded-xl border border-amber-200/35 bg-[rgba(22,18,20,0.92)] px-3 py-2 text-center text-xs font-medium text-[#FFF9F3] shadow-[0_10px_28px_rgba(0,0,0,0.45)]"
        >
          {ctaFeedback}
        </div>
      ) : null}
      <UpgradeModal
        isOpen={isOpen}
        onClose={closeUpgradeModal}
        feature={feature}
        valueProposition={valueProposition}
      />
    </UpgradeModalContext.Provider>
  )
}
